import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { IoCloseSharp } from "react-icons/io5";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../../App";
import CustomDatePicker from "../../../component/CustomDatePicker";
import DocumentPreview from "../../../component/DocumentPreview";
import CreateModal from "../../../component/Model/CreateModel";
import AddRegulatoryCompliancesSkelton from "../../../component/skelton/AddRegulatoryCompliancesSkelton";
import {
  useCreateRegulatoryComplianceMutation,
  useGetRegulatoryComplianceDetailsQuery,
  useUpdateRegulatoryComplianceMutation,
} from "../../../store/api/vendor/regulatoryComplianceSlice";
import { useGetAllVehiclesQuery } from "../../../store/api/vendor/vehiclesSlice";
import { multiPartData2, parseLocalDate } from "../../../utils";
import {
  formatDateToYMD,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import "../../AddVehicle.scss";

const initialState = {
  vehicle_id: "",
  registration_documents: "",
  registration_expiry: null,
  license_documents: "",
  license_expiry: null,
  insurance_documents: "",
  insurance_expiry: null,
  safety_check_documents: "",
  safety_check_expiry: null,
  permit_documents: "",
  permit_expiry: null,
};

const previewInitialState = {
  registration_documents: "",
  registration_document_name: "",
  license_documents: "",
  license_document_name: "",
  insurance_documents: "",
  insurance_document_name: "",
  safety_check_documents: "",
  safety_check_document_name: "",
  permit_documents: "",
  permit_document_name: "",
};

const isValidFileType = (file) => {
  const allowedTypes = ["application/pdf"];
  return allowedTypes.includes(file.type);
};

const AddRegulatoryCompliances = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [preview, setPreview] = useState(previewInitialState);
  const [vehicles, setVehicles] = useState([]);
  const [errors, setErrors] = useState({});

  const { data, isSuccess } = useGetAllVehiclesQuery({});

  // CREATE VEHICLE REGULATORY COMPLIANCE API (FOR SCHOOL | VENDOR)
  const [createRegulatoryCompliance, { isLoading }] =
    useCreateRegulatoryComplianceMutation();

  // UPDATE VEHICLE REGULATORY COMPLIANCE API (FOR SCHOOL | VENDOR)
  const [updateRegulatoryCompliance, { isLoading: isUpdateLoading }] =
    useUpdateRegulatoryComplianceMutation();

  const {
    data: complianceData,
    isSuccess: isSuccessCompliance,
    isLoading: isGetLoading,
  } = useGetRegulatoryComplianceDetailsQuery(id, { skip: !id });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value
        ? ""
        : `${t("errors." + (name === "vehicle_id" ? "vehicle" : name))} ${t(
            "errors.required",
          )}`,
    }));
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({ ...prev, [field]: date || null }));
    setErrors((prev) => ({
      ...prev,
      [field]: date
        ? ""
        : `${t("expiry")} ${t("date")} ${t("errors.required")}`,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate vehicle selection
    if (!formData.vehicle_id) {
      newErrors.vehicle_id = `${t("vehicle")} ${t("errors.required")}`;
    }

    // Validate all required documents and their expiry dates
    const requiredDocuments = [
      { file: "registration_documents", expiry: "registration_expiry" },
      { file: "license_documents", expiry: "license_expiry" },
      { file: "insurance_documents", expiry: "insurance_expiry" },
      { file: "safety_check_documents", expiry: "safety_check_expiry" },
      { file: "permit_documents", expiry: "permit_expiry" },
    ];

    requiredDocuments.forEach(({ file, expiry }) => {
      if (!formData[file] || formData[file] === "") {
        newErrors[file] = `${t("errors." + file)} ${t("errors.required")}`;
      }
      if (!formData[expiry]) {
        newErrors[expiry] =
          `${t("expiry")} ${t("date")} ${t("errors.required")}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { user } = useSelector((state) => state.UserReducer);

  const handleReset = () => {
    setFormData(initialState);
    setPreview(previewInitialState);
    setErrors({});
  };

  const handleClose = () => {
    setShow(false);
    navigate(`/regulatory-compliance`);
  };

  // General file handler to handle file uploads for any field
  const handleFileUpload = (field, e) => {
    const file = e.target.files[0];
    const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
    if (file && isValidFileType(file)) {
      const previewUrl = URL.createObjectURL(file);

      if (file.size > maxSize) {
        toast.error(t("file_size_exceeds"), {
          position: toast?.POSITION?.TOP_RIGHT,
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
      setPreview((prev) => ({
        ...prev,
        [`${field}`]: previewUrl,
        [`${field}_name`]: file.name,
      }));
    } else {
      toast.error(t("only_pdf_allowed"), {
        position: toast?.POSITION?.TOP_RIGHT,
      });
    }
  };

  // General clear handler to clear the document and reset errors and previews
  const clearFile = (field) => {
    if (preview[field]) {
      URL.revokeObjectURL(preview[field]); // Revoke the preview URL to prevent memory leaks
    }

    setFormData((prev) => ({
      ...prev,
      [field]: "",
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setPreview((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    const data = multiPartData2({
      ...formData,
      registration_expiry: formatDateToYMD(formData.registration_expiry),
      license_expiry: formatDateToYMD(formData.license_expiry),
      insurance_expiry: formatDateToYMD(formData.insurance_expiry),
      safety_check_expiry: formatDateToYMD(formData.safety_check_expiry),
      permit_expiry: formatDateToYMD(formData.permit_expiry),
    });
    try {
      let response;
      id
        ? (response = await updateRegulatoryCompliance({
            data,
            id,
          }).unwrap())
        : (response = await createRegulatoryCompliance(data).unwrap());

      if (response.statusCode === 200 || response.statusCode === 201) {
        setShow(true);
      }
    } catch (err) {
      handleApiError(err, t);
    }
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setVehicles(data.data.vehicles);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (isSuccessCompliance && complianceData?.data) {
      setFormData({
        vehicle_id: complianceData?.data?.vehicle_id,
        registration_documents: complianceData?.data?.registration_document,
        registration_expiry: parseLocalDate(
          complianceData?.data?.registration_expiry,
        ),
        license_documents: complianceData?.data?.license_document,
        license_expiry: parseLocalDate(complianceData?.data?.license_expiry),
        insurance_documents: complianceData?.data?.insurance_document,
        insurance_expiry: parseLocalDate(
          complianceData?.data?.insurance_expiry,
        ),
        safety_check_documents: complianceData?.data?.safety_check_document,
        safety_check_expiry: parseLocalDate(
          complianceData?.data?.safety_check_expiry,
        ),
        permit_documents: complianceData?.data?.permit_document,
        permit_expiry: parseLocalDate(complianceData?.data?.permit_expiry),
      });

      setPreview({
        registration_documents: `${server}${complianceData?.data?.registration_document}`,
        license_documents: `${server}${complianceData?.data?.license_document}`,
        insurance_documents: `${server}${complianceData?.data?.insurance_document}`,
        safety_check_documents: `${server}${complianceData?.data?.safety_check_document}`,
        permit_documents: complianceData?.data?.permit_document
          ? `${server}${complianceData?.data?.permit_document}`
          : "",
      });
    }
  }, [complianceData, isSuccessCompliance]);

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        <div className="dbSubHd mb-4">
          {t("regulatory")} {t("compliance")}
        </div>

        {isGetLoading ? (
          <AddRegulatoryCompliancesSkelton />
        ) : (
          <>
            <ul className="form d-flex flex-wrap p-0">
              {/* Vehicle Selection */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("select")} {t("vehicle")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  name="vehicle_id"
                  className="formSelect"
                  value={formData.vehicle_id}
                  onChange={handleChange}
                  style={{
                    borderColor: errors?.vehicle_id && " #dc3545",
                  }}
                  // disabled={!!id}
                >
                  <option value="">
                    {t("select")} {t("vehicle")}...
                  </option>
                  {vehicles.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.vehicle_type?.name} - {item.license_number}
                    </option>
                  ))}
                </select>
                {errors?.vehicle_id && (
                  <span className="text-danger small">
                    {errors?.vehicle_id}
                  </span>
                )}
              </li>

              {/* Registration Document */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("registration")} {t("document")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="document-upload-container">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="uplaodBox">
                      <Button className="upload-btn">
                        {t("btns.upload")}
                        <input
                          type="file"
                          className="file"
                          onChange={(e) =>
                            handleFileUpload("registration_documents", e)
                          }
                          onClick={(e) => (e.target.value = null)}
                          accept=".pdf"
                        />
                      </Button>
                    </div>
                    {formData.registration_documents && (
                      <Button
                        variant=""
                        className="p-0 custom-text"
                        onClick={() => clearFile("registration_documents")}
                        title={`${t("remove")} ${t("file")}`}
                      >
                        <RiDeleteBin5Line />
                      </Button>
                    )}
                  </div>
                  {preview.registration_documents ? (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-secondary">
                          {preview.registration_document_name}
                        </span>
                      </div>
                      <DocumentPreview
                        preview={preview.registration_documents}
                      />
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">
                      {t("upload_pdf_file")} (Max 5MB)
                    </div>
                  )}
                </div>
                {errors?.registration_documents && (
                  <span className="text-danger small d-block">
                    {errors.registration_documents}
                  </span>
                )}
                <div className="mt-3">
                  <CustomDatePicker
                    label={`${t("registration")} ${t("document")} Expiry Date`}
                    selected={formData.registration_expiry}
                    onChange={handleDateChange("registration_expiry")}
                    minDate={new Date()}
                    error={errors?.registration_expiry}
                  />
                </div>
              </li>

              {/* License Document */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("license")} {t("document")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="document-upload-container">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="uplaodBox">
                      <Button className="upload-btn">
                        {t("btns.upload")}
                        <input
                          type="file"
                          className="file"
                          onChange={(e) =>
                            handleFileUpload("license_documents", e)
                          }
                          onClick={(e) => (e.target.value = null)}
                          accept=".pdf"
                        />
                      </Button>
                    </div>
                    {formData.license_documents && (
                      <Button
                        variant=""
                        className="p-0 custom-text"
                        onClick={() => clearFile("license_documents")}
                        style={{ color: "#dc3545", fontSize: "18px" }}
                        title={`${t("remove")} ${t("file")}`}
                      >
                        <RiDeleteBin5Line />
                      </Button>
                    )}
                  </div>
                  {preview.license_documents ? (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-secondary">
                          {preview.license_document_name}
                        </span>
                      </div>
                      <DocumentPreview preview={preview.license_documents} />
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">
                      {t("upload_pdf_file")} (Max 5MB)
                    </div>
                  )}
                </div>
                {errors?.license_documents && (
                  <span className="text-danger small d-block">
                    {errors.license_documents}
                  </span>
                )}
                <div className="mt-3">
                  <CustomDatePicker
                    label={`${t("license")} ${t("document")} Expiry Date`}
                    selected={formData.license_expiry}
                    onChange={handleDateChange("license_expiry")}
                    minDate={new Date()}
                    error={errors?.license_expiry}
                  />
                </div>
              </li>

              {/* Insurance Document */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("insurance")} {t("document")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="document-upload-container">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="uplaodBox">
                      <Button className="upload-btn">
                        {t("btns.upload")}
                        <input
                          type="file"
                          className="file"
                          onChange={(e) =>
                            handleFileUpload("insurance_documents", e)
                          }
                          onClick={(e) => (e.target.value = null)}
                          accept=".pdf"
                        />
                      </Button>
                    </div>
                    {formData.insurance_documents && (
                      <Button
                        variant=""
                        className="p-0 custom-text"
                        onClick={() => clearFile("insurance_documents")}
                        style={{ color: "#dc3545", fontSize: "18px" }}
                        title={`${t("remove")} ${t("file")}`}
                      >
                        <RiDeleteBin5Line />
                      </Button>
                    )}
                  </div>
                  {preview.insurance_documents ? (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-secondary">
                          {preview.insurance_document_name}
                        </span>
                      </div>
                      <DocumentPreview preview={preview.insurance_documents} />
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">
                      {t("upload_pdf_file")} (Max 5MB)
                    </div>
                  )}
                </div>
                {errors?.insurance_documents && (
                  <span className="text-danger small d-block">
                    {errors.insurance_documents}
                  </span>
                )}
                <div className="mt-3">
                  <CustomDatePicker
                    label={`${t("insurance")} ${t("document")} Expiry Date`}
                    selected={formData.insurance_expiry}
                    onChange={handleDateChange("insurance_expiry")}
                    minDate={new Date()}
                    error={errors?.insurance_expiry}
                  />
                </div>
              </li>

              {/* Safety Check Document */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  {t("safety")} {t("check")} {t("document")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="document-upload-container">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="uplaodBox">
                      <Button className="upload-btn">
                        {t("btns.upload")}
                        <input
                          type="file"
                          className="file"
                          onChange={(e) =>
                            handleFileUpload("safety_check_documents", e)
                          }
                          onClick={(e) => (e.target.value = null)}
                          accept=".pdf"
                        />
                      </Button>
                    </div>
                    {formData.safety_check_documents && (
                      <Button
                        variant=""
                        className="p-0 custom-text"
                        onClick={() => clearFile("safety_check_documents")}
                        style={{ color: "#dc3545", fontSize: "18px" }}
                        title={`${t("remove")} ${t("file")}`}
                      >
                        <RiDeleteBin5Line />
                      </Button>
                    )}
                  </div>
                  {preview.safety_check_documents ? (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-secondary">
                          {preview.safety_check_document_name}
                        </span>
                      </div>
                      <DocumentPreview
                        preview={preview.safety_check_documents}
                      />
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">
                      {t("upload_pdf_file")} (Max 5MB)
                    </div>
                  )}
                </div>
                {errors?.safety_check_documents && (
                  <span className="text-danger small d-block">
                    {errors.safety_check_documents}
                  </span>
                )}
                <div className="mt-3">
                  <CustomDatePicker
                    label={`${t("safety")} ${t("check")} ${t("document")} Expiry Date`}
                    selected={formData.safety_check_expiry}
                    onChange={handleDateChange("safety_check_expiry")}
                    minDate={new Date()}
                    error={errors?.safety_check_expiry}
                  />
                </div>
              </li>

              {/* DOT #/Permit Document */}
              <li className="col-12 col-md-6 col-lg-4 mb-4">
                <label className="form-label fw-semibold">
                  DOT #/Permit {t("document")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="document-upload-container">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="uplaodBox">
                      <Button className="upload-btn">
                        {t("btns.upload")}
                        <input
                          type="file"
                          className="file"
                          onChange={(e) =>
                            handleFileUpload("permit_documents", e)
                          }
                          onClick={(e) => (e.target.value = null)}
                          accept=".pdf"
                        />
                      </Button>
                    </div>
                    {formData.permit_documents && (
                      <Button
                        variant=""
                        className="p-0 custom-text"
                        onClick={() => clearFile("permit_documents")}
                        style={{ color: "#dc3545", fontSize: "18px" }}
                        title={`${t("remove")} ${t("file")}`}
                      >
                        <RiDeleteBin5Line />
                      </Button>
                    )}
                  </div>
                  {preview.permit_documents ? (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="badge bg-secondary">
                          {preview.permit_document_name}
                        </span>
                      </div>
                      <DocumentPreview preview={preview.permit_documents} />
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">
                      {t("upload_pdf_file")} (Max 5MB)
                    </div>
                  )}
                </div>
                {errors?.permit_documents && (
                  <span className="text-danger small d-block">
                    {errors.permit_documents}
                  </span>
                )}
                <div className="mt-3">
                  <CustomDatePicker
                    label="DOT #/Permit Expiry Date"
                    selected={formData.permit_expiry}
                    onChange={handleDateChange("permit_expiry")}
                    minDate={new Date()}
                    error={errors?.permit_expiry}
                  />
                </div>
              </li>
            </ul>

            <div className="pt-4 mt-3 border-top text-end">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-3 px-4 py-2"
              >
                {t("btns.reset")}
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || isUpdateLoading}
                className="px-4 py-2"
              >
                {isLoading || isUpdateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">{t("loading")}...</span>
                    </span>
                  </>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal show={show} centered>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          title={`${t("regulatoryCompliance")} ${id ? t("updated") : t("added")} ${t("successfully")}`}
          text={`${id ? t("regulatoryComplianceUpdated") : t("regulatoryComplianceAdded")}`}
          onClose={handleClose}
        />
      </Modal>

      <style jsx>{`
        .upload-btn {
          backgroundcolor: "#f8f9fa";
          border: "1px solid #ced4da";
          color: "#495057";
          padding: "8px 16px";
          borderradius: "6px";
          fontsize: "14px";
          fontweight: "500";
          width: 100%;
          cursor: pointer;
          text-transform: capitalize;
        }
      `}</style>
    </>
  );
};

export default AddRegulatoryCompliances;
